import React from "react";
import { Box } from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { dt } from "../../../../shared/themes/redesignTokens";
import useTranslation from "../../../../shared/hooks/useTranslation";
import { OfferStatusCode } from "../../models/models";
import { getProviderGuidanceCategory, guidanceCategoryColors, GuidanceCategory } from "../../../shared/utils/guidanceCategories";

interface OfferStatusGuidanceBannerProps {
  statusCode: OfferStatusCode;
  isOwner?: boolean;
  customerName?: string;
  providerName?: string;
  confirmedArrivalDate?: string;
  reviewRating?: number;
}

/**
 * Returns the guidance category for an offer status from the owner perspective.
 */
const getOwnerOfferGuidanceCategory = (statusCode: number): GuidanceCategory => {
  const S = OfferStatusCode;
  switch (statusCode) {
    case S.sent:
    case S.viewed:
    case S.negotiating:
      return "action"; // Owner should review and respond
    case S.awaitingConfirmation:
      return "waiting"; // Waiting for provider to confirm
    case S.confirmed:
    case S.workInProgress:
      return "info";
    case S.workCompleted:
      return "action"; // Owner should confirm completion
    case S.serviceCompleted:
      return "success";
    case S.rejected:
    case S.expired:
    case S.withdrawn:
      return "terminal";
    case S.archived:
      return "muted";
    default:
      return "info";
  }
};

const OfferStatusGuidanceBanner: React.FC<OfferStatusGuidanceBannerProps> = ({
  statusCode,
  isOwner = false,
  customerName,
  providerName,
  confirmedArrivalDate,
  reviewRating,
}) => {
  const { translate } = useTranslation(["serviceOffers"]);

  // Don't show banner for draft
  if (statusCode === OfferStatusCode.draft) return null;

  const category = isOwner
    ? getOwnerOfferGuidanceCategory(statusCode)
    : getProviderGuidanceCategory(statusCode);
  const colors = guidanceCategoryColors[category];

  const getMessage = (): { description: string; actionPrompt?: string } => {
    const S = OfferStatusCode;

    if (isOwner) {
      // Owner perspective messages
      const name = providerName || "";
      switch (statusCode) {
        case S.sent:
        case S.viewed:
        case S.negotiating:
          return {
            description: translate("ownerStatusMessages.reviewing.description", { providerName: name }),
            actionPrompt: translate("ownerStatusMessages.reviewing.actionPrompt"),
          };
        case S.awaitingConfirmation:
          return {
            description: translate("ownerStatusMessages.awaitingConfirmation.description", { providerName: name }),
          };
        case S.confirmed:
          return {
            description: translate("ownerStatusMessages.confirmed.description", { providerName: name }),
          };
        case S.workInProgress:
          return {
            description: translate("ownerStatusMessages.workInProgress.description", { providerName: name }),
          };
        case S.workCompleted:
          return {
            description: translate("ownerStatusMessages.workCompleted.description", { providerName: name }),
            actionPrompt: translate("ownerStatusMessages.workCompleted.actionPrompt"),
          };
        case S.serviceCompleted:
          return {
            description: translate("ownerStatusMessages.serviceCompleted.description"),
          };
        case S.rejected:
          return { description: translate("ownerStatusMessages.rejected.description") };
        case S.expired:
          return { description: translate("ownerStatusMessages.expired.description") };
        case S.withdrawn:
          return { description: translate("ownerStatusMessages.withdrawn.description", { providerName: name }) };
        case S.archived:
          return { description: translate("ownerStatusMessages.archived.description") };
        default:
          return { description: "" };
      }
    }

    // Provider perspective messages (existing)
    switch (statusCode) {
      case S.sent:
        return { description: translate("statusMessages.sent.description") };
      case S.viewed:
        return { description: translate("statusMessages.viewed.description") };
      case S.negotiating:
        return { description: translate("statusMessages.negotiating.description") };
      case S.awaitingConfirmation:
        return {
          description: translate("statusMessages.awaitingConfirmation.description"),
          actionPrompt: translate("statusMessages.awaitingConfirmation.actionPrompt"),
        };
      case S.confirmed:
        return {
          description: translate("statusMessages.confirmed.description"),
          actionPrompt: translate("statusMessages.confirmed.actionPrompt"),
        };
      case S.workInProgress:
        return {
          description: translate("statusMessages.workInProgress.description"),
          actionPrompt: translate("statusMessages.workInProgress.actionPrompt"),
        };
      case S.workCompleted:
        return { description: translate("statusMessages.workCompleted.description") };
      case S.serviceCompleted:
        return {
          description: translate("statusMessages.serviceCompleted.description"),
          actionPrompt: translate("statusMessages.serviceCompleted.actionPrompt"),
        };
      case S.rejected:
        return { description: translate("statusMessages.rejected.description") };
      case S.expired:
        return { description: translate("statusMessages.expired.description") };
      case S.withdrawn:
        return { description: translate("statusMessages.withdrawn.description") };
      case S.archived:
        return { description: translate("statusMessages.archived.description") };
      default:
        return { description: "" };
    }
  };

  const msg = getMessage();
  if (!msg.description) return null;

  return (
    <Box
      sx={{
        backgroundColor: colors.bg,
        borderLeft: `3px solid ${colors.border}`,
        borderRadius: "0 8px 8px 0",
        padding: "14px 18px",
        display: "flex",
        alignItems: "flex-start",
        gap: 1.5,
      }}
    >
      <InfoOutlinedIcon
        sx={{
          fontSize: 18,
          color: colors.border,
          mt: "1px",
          flexShrink: 0,
        }}
      />
      <Box>
        <Box
          sx={{
            fontSize: "13px",
            fontWeight: 500,
            fontFamily: dt.font,
            color: colors.text,
            lineHeight: 1.5,
          }}
        >
          {msg.description}
        </Box>
        {msg.actionPrompt && (
          <Box
            sx={{
              fontSize: "12px",
              fontWeight: 600,
              fontFamily: dt.font,
              color: colors.border,
              mt: "4px",
            }}
          >
            {msg.actionPrompt}
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default OfferStatusGuidanceBanner;
